import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaCog, FaFileAlt, FaQuestionCircle, FaSave } from 'react-icons/fa';
import { AuthContext, axiosInstance } from './AuthContext';
import NavigationBar from './components/NavigationBar';
import LoadingSpinner from './components/LoadingSpinner';
import CustomScreeningWorkspace from './components/CustomScreeningWorkspace';
import CustomStrategyEditor from './components/CustomStrategyEditor';
import AskPinngroStrategyBuilder from './components/AskPinngroStrategyBuilder';
import GuidedStrategyBuilder from './components/GuidedStrategyBuilder';
import usePremiumEntitlement from './hooks/usePremiumEntitlement';
import { readSimpleBotSetup, simpleBotSetupOwnerFromToken, writeSimpleBotSetup } from './simpleBotSetup';
import { formatPercent, validateAllocationPercent } from './advancedTradingSettings';
import { shouldShowOuterStrategySubmit } from './strategyBuilderPresentation';
import { strategyDisplayName } from './beginnerEducation';

const ReadOnlyValue = ({ label, value, help }) => (
  <div role="group" aria-label={label} className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950/50">
    <p className="text-xs font-black uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
    <p className="mt-1 break-words font-bold text-slate-900 dark:text-slate-100">{value}</p>
    {help && <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">{help}</p>}
  </div>
);

const SettingsPage = () => {
  const { token, isTokenVerified, setToken, verifyAndRefreshTokenImmediate } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ownerScope = simpleBotSetupOwnerFromToken(token);
  const premiumQuery = usePremiumEntitlement(Boolean(token && isTokenVerified));
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'trading' ? 'trading' : 'screening');
  const [showAdvancedTools, setShowAdvancedTools] = useState(searchParams.get('tab') === 'trading');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [factoryStrategiesAvailable, setFactoryStrategiesAvailable] = useState(false);
  const [definitions, setDefinitions] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [selectedDefinitionId, setSelectedDefinitionId] = useState(searchParams.get('screeningDefinition') || '');
  const [ownedBots, setOwnedBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState(searchParams.get('botId') || '');
  const [advancedAuthority, setAdvancedAuthority] = useState(null);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [allocationInput, setAllocationInput] = useState('');
  const [savedAllocation, setSavedAllocation] = useState('');
  const [allocationError, setAllocationError] = useState('');
  const [savingAllocation, setSavingAllocation] = useState(false);
  const [customStrategies, setCustomStrategies] = useState([]);
  const [customStrategyTemplate, setCustomStrategyTemplate] = useState(null);
  const [strategyEditorOpen, setStrategyEditorOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const [strategyName, setStrategyName] = useState('');
  const [strategyConfiguration, setStrategyConfiguration] = useState('');
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [strategyError, setStrategyError] = useState('');
  const [strategyConfirmation, setStrategyConfirmation] = useState('');
  const [strategyBuildPath, setStrategyBuildPath] = useState('guided');
  const [strategyReview, setStrategyReview] = useState(null);
  const [strategyIntent, setStrategyIntent] = useState(null);
  const [strategyPreview, setStrategyPreview] = useState(null);
  const intentPreviewSequence = useRef(0);
  const [derivationSource, setDerivationSource] = useState(null);
  const [savedDerivedStrategy, setSavedDerivedStrategy] = useState(null);
  const [derivationDiff, setDerivationDiff] = useState(null);
  const [reviewingDerivation, setReviewingDerivation] = useState(false);
  const [handledDerivationId, setHandledDerivationId] = useState('');
  const [evidenceComparison, setEvidenceComparison] = useState(null);
  const [evidenceComparisonId, setEvidenceComparisonId] = useState('');
  const [evidenceComparisonError, setEvidenceComparisonError] = useState('');
  const [evidenceComparisonLoading, setEvidenceComparisonLoading] = useState(false);
  const deriveFrom = searchParams.get('deriveFrom');
  const guidedDraftStorageKey = ownerScope ? `pinngro.guided-strategy-draft.v1.${ownerScope}` : null;
  const showOuterStrategySubmit = shouldShowOuterStrategySubmit({
    strategyBuildPath,
    editingStrategyId: editingStrategy?.id,
    hasDerivationSource: Boolean(derivationSource),
  });

  const refreshDefinitions = useCallback(async () => {
    const response = await axiosInstance.get('/api/screening/definitions', { params: { lifecycle: 'all' } });
    setDefinitions(response.data.definitions || []);
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token) {
        const stored = localStorage.getItem('token');
        if (stored) setToken(stored);
        else navigate('/login');
        return;
      }
      const verified = await verifyAndRefreshTokenImmediate(true);
      if (!verified) {
        navigate('/login');
        return;
      }
      try {
        const [botsResponse, definitionResponse, catalogResult, strategyResponse, customResponse, customTemplateResponse] = await Promise.all([
          axiosInstance.get('/api/bots', { params: { include_archived: false } }),
          axiosInstance.get('/api/screening/definitions', { params: { lifecycle: 'all' } }),
          axiosInstance.get('/api/screening/definitions/catalog')
            .then((response) => ({ response, error: null }))
            .catch((error) => ({ response: null, error })),
          axiosInstance.get('/api/bots/trading-strategies').catch(() => null),
          axiosInstance.get('/api/strategy-definitions').catch(() => ({ data: { strategies: [] } })),
          axiosInstance.get('/api/bots/custom-strategy-template').catch(() => null),
        ]);
        if (!active) return;
        const bots = Array.isArray(botsResponse.data) ? botsResponse.data : [];
        setOwnedBots(bots);
        setSelectedBotId((current) => {
          if (current && bots.some((bot) => String(bot.id) === String(current))) return String(current);
          return bots[0]?.id ? String(bots[0].id) : '';
        });
        setDefinitions(definitionResponse.data?.definitions || []);
        setCatalog(catalogResult.response?.data || null);
        if (catalogResult.error?.response?.data?.detail?.code === 'screening_data_unavailable') {
          setMessage('Stock screening data is temporarily unavailable. Saved immutable definitions remain visible, but categorical authoring and evaluation are disabled.');
        } else if (catalogResult.error) {
          setMessage('The Custom Screening field catalog is temporarily unavailable. Saved immutable definitions remain visible.');
        }
        setFactoryStrategiesAvailable(Boolean(strategyResponse));
        setCustomStrategies(customResponse.data?.strategies || []);
        setCustomStrategyTemplate(customTemplateResponse?.data || null);
      } catch (error) {
        if (active) setMessage(error.response?.data?.detail || 'Strategy Workshop is temporarily unavailable.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [navigate, setToken, token, verifyAndRefreshTokenImmediate]);

  useEffect(() => {
    let active = true;
    if (!token || !isTokenVerified || !deriveFrom || handledDerivationId === deriveFrom) {
      return () => { active = false; };
    }
    axiosInstance.get(`/api/strategy-definitions/${encodeURIComponent(deriveFrom)}/derivation-draft`)
      .then((response) => {
        if (!active) return;
        const draft = response.data;
        setActiveTab('trading');
        setShowAdvancedTools(true);
        setEditingStrategy(null);
        setDerivationSource(draft.source || null);
        setDerivationDiff(null);
        setHandledDerivationId(deriveFrom);
        setStrategyName(draft.suggested_name || 'My strategy');
        setStrategyConfiguration(JSON.stringify(draft.strategy_configuration || {}, null, 2));
        setStrategyError('');
        setStrategyBuildPath('guided');
        setStrategyReview(null);
        setStrategyIntent(null);
        setStrategyConfirmation('Review the inherited rules, make any supported changes, then save your own immutable strategy.');
        setStrategyEditorOpen(true);
      })
      .catch((error) => {
        if (!active) return;
        const detail = error.response?.data?.detail;
        setStrategyError(detail?.message || detail || 'Pinngro could not prepare that strategy for derivation.');
        setActiveTab('trading');
        setShowAdvancedTools(true);
      });
    return () => { active = false; };
  }, [deriveFrom, handledDerivationId, isTokenVerified, token]);

  useEffect(() => {
    let active = true;
    if (!selectedBotId) {
      setAdvancedAuthority(null);
      setAllocationInput('');
      setSavedAllocation('');
      return () => { active = false; };
    }
    setAdvancedLoading(true);
    setAllocationError('');
    axiosInstance.get(`/api/bots/${selectedBotId}/advanced-settings`)
      .then((response) => {
        if (!active) return;
        setAdvancedAuthority(response.data);
        const allocation = String(response.data?.risk_settings?.maximum_practice_allocation?.value ?? '');
        setAllocationInput(allocation);
        setSavedAllocation(allocation);
      })
      .catch((error) => {
        if (!active) return;
        setAdvancedAuthority(null);
        const detail = error.response?.data?.detail;
        setMessage(detail?.message || detail || 'This bot’s authoritative trading details are unavailable.');
      })
      .finally(() => { if (active) setAdvancedLoading(false); });
    return () => { active = false; };
  }, [selectedBotId]);

  const useSymbols = (symbols, definition) => {
    const draft = readSimpleBotSetup(ownerScope);
    writeSimpleBotSetup({ ...draft, symbols }, ownerScope);
    navigate(`/bots?customScreening=${encodeURIComponent(definition.id)}#hire`);
  };

  const saveRiskSettings = async () => {
    const risk = advancedAuthority?.risk_settings?.maximum_practice_allocation;
    const validation = validateAllocationPercent(allocationInput, risk?.minimum, risk?.maximum);
    setAllocationError(validation);
    if (validation || !risk?.editable) return;
    setSavingAllocation(true);
    try {
      await axiosInstance.patch(`/api/bots/${selectedBotId}`, {
        risk_budget_pct: Number(allocationInput),
      });
      const response = await axiosInstance.get(`/api/bots/${selectedBotId}/advanced-settings`);
      setAdvancedAuthority(response.data);
      const allocation = String(response.data.risk_settings.maximum_practice_allocation.value);
      setAllocationInput(allocation);
      setSavedAllocation(allocation);
      setMessage(`Risk settings saved for ${response.data.bot.name}.`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setMessage(detail?.message || detail || 'Risk settings could not be saved.');
    } finally {
      setSavingAllocation(false);
    }
  };

  const openCreateStrategy = () => {
    setEditingStrategy(null);
    setDerivationSource(null);
    setSavedDerivedStrategy(null);
    setDerivationDiff(null);
    setStrategyName('');
    setStrategyConfiguration(customStrategyTemplate?.strategy_configuration
      ? JSON.stringify(customStrategyTemplate.strategy_configuration, null, 2) : '');
    setStrategyError('');
    setStrategyConfirmation('');
    setStrategyBuildPath('choice');
    setStrategyReview(null);
    setStrategyIntent(null);
    setStrategyPreview(null);
    setStrategyEditorOpen(true);
  };

  const openEditStrategy = (strategy) => {
    setEditingStrategy(strategy);
    setDerivationSource(null);
    setSavedDerivedStrategy(null);
    setDerivationDiff(null);
    setStrategyName(strategy.strategy_name);
    setStrategyConfiguration(JSON.stringify(strategy.canonical_configuration, null, 2));
    setStrategyError('');
    setStrategyConfirmation('');
    setStrategyBuildPath('guided');
    setStrategyReview(null);
    setStrategyIntent(null);
    setStrategyPreview(null);
    setStrategyEditorOpen(true);
  };

  const useAskPinngroDraft = (result) => {
    const preview = result?.compiled_preview;
    if (!preview?.strategy_configuration) {
      setStrategyError('Ask Pinngro did not return a server-validated draft. Nothing has changed.');
      return;
    }
    setStrategyName(preview.strategy_name || result.intent?.strategy_name_suggestion || 'My strategy');
    setStrategyConfiguration(JSON.stringify(preview.strategy_configuration, null, 2));
    setStrategyReview(result.plain_english || null);
    setStrategyIntent(result.intent || null);
    setStrategyPreview(result);
    setStrategyBuildPath('guided');
    setStrategyError('');
    setStrategyConfirmation('Ask Pinngro filled an unsaved draft. Review it below; only Create strategy will save it.');
  };

  const updateStrategyConfiguration = (configuration) => {
    setStrategyConfiguration(configuration);
    setStrategyIntent(null);
    setStrategyPreview(null);
    if (strategyReview) {
      setStrategyReview(null);
      setStrategyConfirmation('Your deterministic rules changed after Ask Pinngro interpreted them. Review the updated controls before creating the strategy.');
    }
  };

  const updateStrategyIntent = async (intent) => {
    const sequence = intentPreviewSequence.current + 1;
    intentPreviewSequence.current = sequence;
    setStrategyIntent(intent);
    setStrategyError('');
    try {
      const response = await axiosInstance.post('/api/strategy-definitions/intent-preview', {
        intent,
        strategy_name: strategyName.trim() || intent.strategy_name_suggestion,
      });
      if (sequence !== intentPreviewSequence.current) return;
      setStrategyConfiguration(JSON.stringify(response.data.compiled_preview.strategy_configuration, null, 2));
      setStrategyReview(response.data.plain_english || null);
      setStrategyPreview(response.data);
      setStrategyConfirmation('Your updated plain-English rules were validated and compiled by Pinngro. Nothing is saved until you choose Create strategy.');
    } catch (error) {
      if (sequence !== intentPreviewSequence.current) return;
      const detail = error.response?.data?.detail;
      setStrategyError(detail?.message || 'Pinngro could not validate that change. Your strategy has not been saved.');
    }
  };

  const saveCustomStrategy = async (event) => {
    event.preventDefault();
    if (savingStrategy) return;
    setStrategyError('');
    let configuration;
    try {
      configuration = JSON.parse(strategyConfiguration);
    } catch {
      setStrategyError('The Advanced strategy definition must be valid JSON.');
      return;
    }
    setSavingStrategy(true);
    try {
      const path = derivationSource
        ? '/api/strategy-definitions/derive'
        : editingStrategy
          ? `/api/strategy-definitions/${editingStrategy.id}/revisions`
          : '/api/strategy-definitions';
      const response = await axiosInstance.post(path, {
        strategy_name: strategyName.trim(),
        strategy_configuration: configuration,
        ...(derivationSource ? { source_definition_id: derivationSource.id } : {}),
      });
      const refreshed = await axiosInstance.get('/api/strategy-definitions');
      setCustomStrategies(refreshed.data?.strategies || []);
      setEditingStrategy(response.data);
      if (guidedDraftStorageKey) localStorage.removeItem(guidedDraftStorageKey);
      setStrategyConfirmation(derivationSource
        ? `Your version of ${derivationSource.display_name} has been saved as an immutable strategy. You can now select ${response.data.strategy_name} for an eligible Draft bot.`
        : editingStrategy
        ? `A new immutable version of ${response.data.strategy_name} has been created. Existing bot and Backtest history remains on its recorded version.`
        : `Your strategy has been created. You can now select ${response.data.strategy_name} when creating or configuring an eligible Draft bot.`);
      setDerivationSource(null);
      setSavedDerivedStrategy(derivationSource ? response.data : null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setStrategyError(detail?.message || (typeof detail === 'string' ? detail : 'The strategy could not be created. Your rules are still here.'));
    } finally {
      setSavingStrategy(false);
    }
  };

  const reviewDerivationChanges = async () => {
    if (!derivationSource || reviewingDerivation) return;
    let configuration;
    try {
      configuration = JSON.parse(strategyConfiguration);
    } catch {
      setStrategyError('The Advanced strategy definition must be valid JSON before Pinngro can review changes.');
      return;
    }
    setReviewingDerivation(true);
    setStrategyError('');
    try {
      const response = await axiosInstance.post(
        `/api/strategy-definitions/${encodeURIComponent(derivationSource.id)}/derivation-diff`,
        { strategy_configuration: configuration },
      );
      setDerivationDiff(response.data?.changes || []);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setStrategyError(detail?.message || (typeof detail === 'string' ? detail : 'Pinngro could not validate these changes. Your draft has not been saved.'));
    } finally {
      setReviewingDerivation(false);
    }
  };

  const compareDerivedEvidence = async (strategyId) => {
    if (!strategyId || evidenceComparisonLoading) return;
    setEvidenceComparisonLoading(true);
    setEvidenceComparisonError('');
    setEvidenceComparison(null);
    setEvidenceComparisonId(strategyId);
    try {
      const response = await axiosInstance.get(`/api/strategy-definitions/${encodeURIComponent(strategyId)}/evidence-comparison`, { timeout: 15000 });
      setEvidenceComparison(response.data || null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setEvidenceComparisonError(detail?.message || (typeof detail === 'string' ? detail : 'Pinngro could not load the recorded forward evidence.'));
    } finally {
      setEvidenceComparisonLoading(false);
    }
  };

  const tabs = [
    { id: 'screening', label: 'Custom Screening' },
    { id: 'trading', label: 'Strategy & Risk' },
    ...(showAdvancedTools ? [
      ...(factoryStrategiesAvailable ? [{ id: 'durable-backtesting', label: 'Backtesting' }] : []),
    ] : []),
  ];

  if (!isTokenVerified || loading) return <LoadingSpinner fullScreen showLabel label="Loading strategy workshop…" />;

  return (
    <div className="min-h-screen flex flex-col">
      <NavigationBar />
      <main className="flex-1 bg-gray-50 p-6 pt-20 dark:bg-gray-900">
        <section className="mx-auto max-w-6xl rounded-3xl bg-white p-6 shadow-lg dark:bg-gray-800 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Strategy Workshop</p>
              <h1 className="mt-2 text-3xl font-black">Advanced candidate and strategy tools</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">Stock Focus is a factory screen. Custom Screening is your immutable candidate-selection logic. Trading Strategy independently decides when to buy or sell.</p>
            </div>
            <div className="flex gap-4 text-sm font-bold text-blue-600 dark:text-blue-300">
              <Link to="/help"><FaQuestionCircle className="mr-1 inline" />Help</Link>
              <Link to="/terms"><FaFileAlt className="mr-1 inline" />Terms</Link>
            </div>
          </div>

          {message && <p className="mt-5 rounded-xl bg-slate-100 p-3 text-sm dark:bg-slate-900">{typeof message === 'string' ? message : message.message}</p>}

          <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-blue-100 bg-blue-50 p-5 dark:border-blue-900 dark:bg-blue-950/30 sm:flex-row sm:items-center">
            <div className="flex-1">
              <p className="font-black">{showAdvancedTools ? 'Advanced tools are visible' : 'Custom Screening workspace'}</p>
              <p className="mt-1 text-sm">Structured controls come first. Advanced technical JSON remains available through progressive disclosure; YAML and executable expressions are not accepted.</p>
            </div>
            <button type="button" onClick={() => { setShowAdvancedTools((current) => !current); setActiveTab('screening'); }} className="rounded-xl border border-blue-300 bg-white px-4 py-3 text-sm font-bold text-blue-700 dark:border-blue-800 dark:bg-slate-900 dark:text-blue-300"><FaCog className="mr-2 inline" />{showAdvancedTools ? 'Return to screening' : 'Show advanced tools'}</button>
            <Link to="/bots#hire" className="rounded-xl bg-blue-600 px-4 py-3 text-center text-sm font-bold text-white">Return to bot setup</Link>
          </div>

          <nav className="mt-6 flex flex-wrap border-b dark:border-slate-700" aria-label="Strategy Workshop sections">
            {tabs.map((tab) => <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-bold ${activeTab === tab.id ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500'}`}>{tab.label}</button>)}
          </nav>

          <div className="mt-6">
            {activeTab === 'screening' && <CustomScreeningWorkspace definitions={definitions} catalog={catalog} selectedDefinitionId={selectedDefinitionId} onSelectDefinition={setSelectedDefinitionId} onDefinitionsChanged={refreshDefinitions} onUseSymbols={useSymbols} />}

            {activeTab === 'durable-backtesting' && (
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-6 dark:border-blue-900 dark:bg-blue-950/30">
                <h2 className="text-2xl font-black">Deterministic Backtesting</h2>
                <p className="mt-2 text-sm leading-6">Backtests use explicitly selected symbols and pinned historical snapshots. A current Custom Screening result is never silently reused as a historical universe.</p>
                {premiumQuery.data?.entitlements?.premium_backtesting === true ? <Link to="/backtests" className="mt-5 inline-flex rounded-xl bg-blue-600 px-5 py-3 font-bold text-white">Open Backtests</Link> : <Link to="/subscribe" className="mt-5 inline-flex rounded-xl bg-blue-600 px-5 py-3 font-bold text-white">View plans for Backtests</Link>}
              </div>
            )}

            {activeTab === 'trading' && (
              <div className="min-w-0">
                <h2 className="text-2xl font-black">Strategy &amp; Risk</h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  See the strategy your bot follows, adjust its Practice risk where allowed, or create your own strategy.
                </p>

                <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-violet-200 bg-violet-50/60 p-5 dark:border-violet-900 dark:bg-violet-950/20 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="font-black">Your strategies</h3>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Reusable custom rules for eligible Draft bots. Pinngro strategies remain separate and read-only.</p>
                  </div>
                  <button type="button" onClick={openCreateStrategy} className="min-h-11 rounded-xl bg-violet-700 px-5 py-3 font-bold text-white">Create strategy</button>
                </div>

                {strategyConfirmation && <div role="status" aria-live="polite" className="mt-4 rounded-xl bg-emerald-50 p-4 text-sm font-bold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"><p>{strategyConfirmation}</p>{savedDerivedStrategy && <Link to="/bots#hire" className="mt-3 inline-flex min-h-11 items-center rounded-lg border border-emerald-500 bg-white px-3 py-2 text-sm font-black text-emerald-900 dark:bg-slate-900 dark:text-emerald-100">Use in a Practice bot</Link>}</div>}

                {strategyEditorOpen && (
                  <form onSubmit={saveCustomStrategy} className="mt-5 rounded-2xl border border-blue-200 p-4 dark:border-blue-900 sm:p-5" aria-labelledby="custom-strategy-editor-heading">
                    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 id="custom-strategy-editor-heading" className="text-xl font-black">{derivationSource ? `Create your version of ${derivationSource.display_name}` : editingStrategy?.id ? `Edit ${editingStrategy.strategy_name}` : 'Create your strategy'}</h3>
                        {derivationSource && <div className="mt-2 rounded-xl border border-violet-200 bg-violet-50 p-3 text-sm text-violet-950 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-100"><p><strong>Based on:</strong> {derivationSource.display_name}</p><p className="mt-1 text-xs">The source stays unchanged. This draft inherits its structured rules and becomes your own immutable strategy only when you save. Guided controls preserve inherited rule structure; use Advanced technical details only for structured changes that the existing compiler accepts.</p></div>}
                        {editingStrategy?.id && <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Saving changes creates a new version so existing bot and Backtest history remains reproducible.</p>}
                      </div>
                      <button type="button" onClick={() => setStrategyEditorOpen(false)} className="min-h-11 rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold dark:border-slate-700">Close editor</button>
                    </div>
                    {!editingStrategy?.id && !derivationSource && strategyBuildPath === 'choice' ? (
                      <AskPinngroStrategyBuilder
                        client={axiosInstance}
                        onUseDraft={useAskPinngroDraft}
                        onChooseGuided={() => setStrategyBuildPath('guided')}
                        onChooseAdvanced={() => setStrategyBuildPath('advanced')}
                      />
                    ) : !editingStrategy?.id && !derivationSource && strategyBuildPath === 'guided' ? (
                      <GuidedStrategyBuilder
                        client={axiosInstance}
                        initialIntent={strategyIntent}
                        initialPreview={strategyPreview}
                        storageKey={guidedDraftStorageKey}
                        saving={savingStrategy}
                        onValidatedDraft={(result) => {
                          setStrategyName(result.compiled_preview?.strategy_name || result.intent?.strategy_name_suggestion || 'My strategy');
                          setStrategyConfiguration(JSON.stringify(result.compiled_preview?.strategy_configuration || {}, null, 2));
                          setStrategyReview(result.plain_english || null);
                          setStrategyIntent(result.intent || null);
                          setStrategyPreview(result);
                          setStrategyError('');
                          setStrategyConfirmation('Your guided rules were validated and compiled by Pinngro. Nothing is saved until you choose Create strategy.');
                        }}
                        onAskPinngro={(intent) => {
                          setStrategyIntent(intent);
                          setStrategyPreview(null);
                          setStrategyBuildPath('ask');
                        }}
                        onChooseBuildMethod={() => {
                          setStrategyBuildPath('choice');
                          setStrategyPreview(null);
                        }}
                      />
                    ) : !editingStrategy?.id && !derivationSource && strategyBuildPath === 'ask' ? (
                      <AskPinngroStrategyBuilder
                        client={axiosInstance}
                        startInAsk
                        currentDraft={strategyIntent}
                        onUseDraft={useAskPinngroDraft}
                        onChooseGuided={() => setStrategyBuildPath('guided')}
                        onChooseAdvanced={() => setStrategyBuildPath('advanced')}
                      />
                    ) : (
                      <>
                        {derivationSource && (
                          <details className="mb-4 rounded-2xl border border-violet-200 bg-violet-50/50 p-4 dark:border-violet-900 dark:bg-violet-950/20">
                            <summary className="cursor-pointer font-black">Describe a change to Ask Pinngro</summary>
                            <div className="mt-4">
                              <AskPinngroStrategyBuilder
                                client={axiosInstance}
                                sourceDefinitionId={derivationSource.id}
                                onUseDraft={useAskPinngroDraft}
                              />
                            </div>
                          </details>
                        )}
                        {strategyReview && (
                          <section aria-labelledby="strategy-review-heading" className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm dark:border-emerald-900 dark:bg-emerald-950/30">
                            <h4 id="strategy-review-heading" className="text-lg font-black">Review your strategy</h4>
                            <p className="mt-2"><strong>When to buy:</strong> {strategyReview.when_to_buy?.join(' ')}</p>
                            <p className="mt-2"><strong>When to sell:</strong> {strategyReview.when_to_take_profit} {strategyReview.other_automatic_sell_rules}</p>
                            <p className="mt-2"><strong>Re-entry:</strong> {strategyReview.reentry}</p>
                            <p className="mt-2"><strong>Safety:</strong> {strategyReview.loss_protection} {strategyReview.runtime_safety}</p>
                            <button type="button" onClick={() => { setStrategyBuildPath('choice'); setStrategyReview(null); }} className="mt-3 min-h-11 rounded-lg border border-emerald-400 px-3 py-2 font-bold">Change rules</button>
                          </section>
                        )}
                        <CustomStrategyEditor
                          strategyName={strategyName}
                          configuration={strategyConfiguration}
                          onStrategyNameChange={setStrategyName}
                          onConfigurationChange={updateStrategyConfiguration}
                          semanticIntent={strategyIntent}
                          onSemanticIntentChange={updateStrategyIntent}
                          parentEvaluatorVersion={editingStrategy?.evaluator_version || derivationSource?.evaluator_version || null}
                          idPrefix="strategy-library"
                          advancedOnly={strategyBuildPath === 'advanced'}
                        />
                      </>
                    )}
                    {strategyError && <p role="alert" className="mt-3 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-700 dark:bg-red-950/40 dark:text-red-300">{strategyError}</p>}
                    {derivationSource && <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50/70 p-4 text-sm dark:border-violet-900 dark:bg-violet-950/20"><div className="flex flex-wrap items-center justify-between gap-3"><div><p className="font-black">Changed from source</p><p className="mt-1 text-xs text-slate-600 dark:text-slate-300">Review this deterministic structured diff before saving. It does not create or modify a strategy.</p></div><button type="button" onClick={reviewDerivationChanges} disabled={reviewingDerivation} className="min-h-11 rounded-lg border border-violet-300 bg-white px-3 py-2 text-sm font-bold text-violet-900 disabled:opacity-60 dark:border-violet-800 dark:bg-slate-900 dark:text-violet-100">{reviewingDerivation ? 'Reviewing…' : 'Review changes'}</button></div>{derivationDiff && (derivationDiff.length ? <ul className="mt-3 space-y-2" aria-label="Changed from source">{derivationDiff.map((change) => <li key={change.path} className="rounded-lg bg-white p-3 text-xs dark:bg-slate-900"><strong>{change.label}</strong>{change.kind === 'added' ? ` added: ${change.derived_display}` : change.kind === 'removed' ? ` removed: ${change.source_display}` : `: ${change.source_display} → ${change.derived_display}`}</li>)}</ul> : <p className="mt-3 text-xs font-bold text-emerald-800 dark:text-emerald-200">No supported rules differ from the inherited source.</p>)}</div>}
                    {showOuterStrategySubmit && <button type="submit" disabled={savingStrategy || strategyName.trim().length < 3 || !strategyConfiguration} className="mt-4 min-h-11 rounded-xl bg-blue-600 px-5 py-3 font-black text-white disabled:cursor-not-allowed disabled:opacity-60">
                      {savingStrategy ? 'Creating…' : derivationSource ? 'Save my version' : editingStrategy?.id ? 'Create new version' : 'Create strategy'}
                    </button>}
                  </form>
                )}

                {customStrategies.length > 0 && (
                  <ul className="mt-5 grid gap-3 sm:grid-cols-2" aria-label="Your reusable strategies">
                    {customStrategies.map((strategy) => (
                      <li key={strategy.id} className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="font-black">{strategy.strategy_name}</p>
                            <p className="mt-1 text-xs text-slate-500">Your strategy · Revision {strategy.revision} · Used by {strategy.used_by_bots} {strategy.used_by_bots === 1 ? 'bot' : 'bots'}</p>
                            {strategy.derivation && <p className="mt-2 text-xs leading-5 text-violet-800 dark:text-violet-200"><strong>Based on:</strong> {strategy.derivation.source_display_name} · Source version {strategy.derivation.source_definition_version}</p>}
                          </div>
                          <div className="flex flex-wrap gap-2"><button type="button" onClick={() => openEditStrategy(strategy)} className="min-h-11 rounded-lg border border-blue-300 px-3 py-2 text-sm font-bold text-blue-700 dark:border-blue-800 dark:text-blue-300">Edit strategy</button>{strategy.derivation && <button type="button" onClick={() => compareDerivedEvidence(strategy.id)} className="min-h-11 rounded-lg border border-violet-300 px-3 py-2 text-sm font-bold text-violet-900 dark:border-violet-800 dark:text-violet-100">Compare with source</button>}</div>
                        </div>
                        {strategy.derivation && evidenceComparisonId === strategy.id && <section className="mt-4 rounded-xl border border-violet-200 bg-violet-50/70 p-4 text-sm dark:border-violet-900 dark:bg-violet-950/20" aria-label="Derived strategy evidence comparison">
                          <h4 className="font-black">Compare with source</h4>
                          {evidenceComparisonLoading && <p className="mt-2 text-slate-600 dark:text-slate-300">Loading recorded forward evidence…</p>}
                          {evidenceComparisonError && <p role="alert" className="mt-2 font-bold text-rose-700 dark:text-rose-300">{evidenceComparisonError}</p>}
                          {evidenceComparison && <><p className="mt-2 text-xs"><strong>Based on:</strong> {evidenceComparison.lineage?.root_source?.name}</p>{evidenceComparison.structured_differences?.length > 0 && <p className="mt-1 text-xs"><strong>Changed from source:</strong> {evidenceComparison.structured_differences.map((item) => `${item.label} ${item.source_display} → ${item.derived_display}`).join('; ')}</p>}<div className="mt-3 grid gap-3 sm:grid-cols-2">{[['My strategy', evidenceComparison.my_strategy], ['Source strategy', evidenceComparison.source_strategy]].map(([heading, metrics]) => <div key={heading} className="rounded-lg bg-white/70 p-3 dark:bg-slate-900/60"><p className="font-black">{heading}</p><p className="mt-1 text-xs">{metrics?.completed_market_sessions_observed || 0} sessions · {metrics?.symbols_evaluated || 0} symbols · {metrics?.unique_completed_session_evaluations || 0} unique evaluations</p><p className="mt-1 text-xs">{metrics?.actionable_signals || 0} actionable signals · {metrics?.proposals || 0} proposals</p><p className="mt-1 text-xs">{metrics?.completed_practice_trades || 0} completed trades · {metrics?.open_positions || 0} open positions</p></div>)}</div>{(evidenceComparison.paired?.comparable_evaluations || 0) === 0 ? <p className="mt-3 text-xs leading-5">No comparable source evidence is available for these sessions yet. Pinngro does not substitute unrelated history or create a replay for this view.</p> : <><p className="mt-3 text-xs leading-5">So far, your strategy has behaved differently in {(evidenceComparison.paired?.source_only_signal || 0) + (evidenceComparison.paired?.derived_only_signal || 0)} of {evidenceComparison.paired?.comparable_evaluations || 0} comparable symbol/session evaluations.</p><ul className="mt-2 grid gap-1 text-xs sm:grid-cols-2"><li>Both: no signal · {evidenceComparison.paired?.both_no_signal || 0}</li><li>Both: signal · {evidenceComparison.paired?.both_signal || 0}</li><li>Source-only signal · {evidenceComparison.paired?.source_only_signal || 0}</li><li>My version only · {evidenceComparison.paired?.derived_only_signal || 0}</li></ul>{evidenceComparison.paired?.differences?.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs">{evidenceComparison.paired.differences.map((item) => <li key={`${item.symbol}-${item.market_session}`}>{item.symbol} · source: {item.source_signal} · my version: {item.derived_signal}</li>)}</ul>}</>}<p className="mt-3 text-xs text-slate-600 dark:text-slate-300">{evidenceComparison.sample_notice}</p></>}
                        </section>}
                      </li>
                    ))}
                  </ul>
                )}

                {ownedBots.length === 0 ? (
                  <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm dark:border-amber-900 dark:bg-amber-950/30">
                    Create a bot to see its current strategy and Practice risk here.
                    <Link to="/bots#hire" className="ml-1 font-bold text-blue-600 underline dark:text-blue-300">Create a bot</Link>
                  </div>
                ) : (
                  <label htmlFor="advanced-bot-context" className="mt-5 block max-w-2xl text-sm font-bold">
                    Bot and strategy context
                    <span id="advanced-bot-context-help" className="mt-1 block text-xs font-normal leading-5 text-slate-500">
                      Advanced settings are bot-scoped. Selecting a bot does not change it.
                    </span>
                    <select
                      id="advanced-bot-context"
                      aria-describedby="advanced-bot-context-help"
                      value={selectedBotId}
                      onChange={(event) => setSelectedBotId(event.target.value)}
                      className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
                    >
                      {ownedBots.map((bot) => (
                        <option key={bot.id} value={bot.id}>
                          {bot.name} — {strategyDisplayName(bot.trading_strategy_family, bot.trading_strategy_name || 'Strategy unavailable')}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {advancedLoading && <LoadingSpinner showLabel label="Loading authoritative strategy settings…" />}

                {!advancedLoading && advancedAuthority && (() => {
                  const framework = advancedAuthority.strategy_framework;
                  const risk = advancedAuthority.risk_settings;
                  const backtesting = advancedAuthority.backtesting_assumptions;
                  const allocation = risk.maximum_practice_allocation;
                  const allocationDirty = allocationInput !== savedAllocation;
                  return (
                    <div className="mt-6 space-y-6" data-testid="advanced-trading-authority">
                      <section aria-labelledby="strategy-framework-heading" className="min-w-0 rounded-2xl border border-violet-200 bg-violet-50/60 p-5 dark:border-violet-900 dark:bg-violet-950/20 sm:p-6">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.16em] text-violet-700 dark:text-violet-300">A. Strategy</p>
                            <h3 id="strategy-framework-heading" className="mt-1 text-xl font-black">Current strategy: {framework.name}</h3>
                            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">The strategy defines when this bot looks to enter or exit. Saving risk settings cannot change these rules.</p>
                          </div>
                          <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-black text-violet-800 dark:bg-slate-900 dark:text-violet-200">{framework.direction} STRATEGY</span>
                        </div>

                        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <ReadOnlyValue label="Strategy" value={framework.name} help={framework.factory_strategy ? 'Researched factory definition' : 'Owned immutable custom definition'} />
                          <ReadOnlyValue
                            label="Find stocks"
                            value={advancedAuthority.bot.stock_selection_mode === 'ALL_STOCKS' ? 'All Stocks — chosen directly by you' : advancedAuthority.bot.stock_focus}
                            help={advancedAuthority.bot.stock_selection_mode === 'ALL_STOCKS' ? 'No Stock Focus profile is attached.' : 'Which stocks this bot considers; separate from its trading rules.'}
                          />
                          <ReadOnlyValue label="Direction" value={framework.direction} help={framework.direction === 'SHORT' ? 'Practice can profit if price falls and lose if it rises.' : 'Practice can profit if price rises and lose if it falls.'} />
                          <ReadOnlyValue label="Trading timeframe" value={framework.timeframe.value} help="Defined by this strategy; intraday choices are not supported here." />
                          <ReadOnlyValue label="Required lookback" value={`${framework.lookback_sessions} sessions`} help="Minimum completed history required before evaluation." />
                          <ReadOnlyValue label="Execution" value={framework.execution.value} help={framework.execution.detail} />
                        </div>

                        {framework.indicators.length > 0 && (
                          <div className="mt-5">
                            <h4 className="font-black">Indicators this strategy actually uses</h4>
                            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                              {framework.indicators.map((indicator) => (
                                <ReadOnlyValue key={indicator.id} label={indicator.label} value={indicator.value} help={indicator.detail} />
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-5 grid gap-3 lg:grid-cols-2">
                          <div className="rounded-xl bg-white p-4 text-sm leading-6 dark:bg-slate-900">
                            <h4 className="font-black">Entry framework</h4>
                            <p className="mt-1 text-slate-600 dark:text-slate-300">{framework.entry_summary}</p>
                          </div>
                          <div className="rounded-xl bg-white p-4 text-sm leading-6 dark:bg-slate-900">
                            <h4 className="font-black">Exit framework</h4>
                            <p className="mt-1 text-slate-600 dark:text-slate-300">{framework.exit_summary}</p>
                          </div>
                          <ReadOnlyValue label="Stop loss" value={framework.stop_loss.display} help={framework.stop_loss.enabled ? 'Triggered by a completed daily close relative to the actual entry fill.' : 'This strategy has no fixed stop-loss rule.'} />
                          <ReadOnlyValue label="Take profit" value={framework.take_profit.display} help={framework.take_profit.enabled ? 'Triggered by a completed daily close relative to the actual entry fill.' : 'This strategy has no fixed take-profit rule.'} />
                          <ReadOnlyValue label="Maximum holding period" value={framework.time_exit.display} help="A time-based exit is shown only when it exists in the canonical strategy." />
                          <ReadOnlyValue label="Market or trend context" value={framework.trend_or_regime} help="This is strategy-defined, not an account-wide toggle." />
                        </div>

                        {(framework.entry_rules.length > 0 || framework.technical_exit_rules.length > 0) && (
                          <details className="mt-4 rounded-xl border border-violet-200 bg-white/80 p-4 text-sm dark:border-violet-800 dark:bg-slate-900/70">
                            <summary className="cursor-pointer font-black">Exact strategy rules in plain language</summary>
                            <div className="mt-3 grid gap-4 lg:grid-cols-2">
                              {framework.entry_rules.length > 0 && (
                                <div>
                                  <h4 className="font-bold">Entry requires {framework.entry_rule_logic === 'any' ? 'any of these' : 'all of these'}</h4>
                                  <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-600 dark:text-slate-300">
                                    {framework.entry_rules.map((rule) => <li key={rule}>{rule}</li>)}
                                  </ul>
                                </div>
                              )}
                              {framework.technical_exit_rules.length > 0 && (
                                <div>
                                  <h4 className="font-bold">Technical exit when {framework.technical_exit_rule_logic === 'all' ? 'all are true' : 'any is true'}</h4>
                                  <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-600 dark:text-slate-300">
                                    {framework.technical_exit_rules.map((rule) => <li key={rule}>{rule}</li>)}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </details>
                        )}

                        <div className="mt-5 rounded-xl border border-violet-200 bg-white/80 p-4 text-sm dark:border-violet-800 dark:bg-slate-900/70">
                          <strong>{framework.factory_strategy ? 'Defined by this strategy.' : 'Saved as an immutable custom revision.'}</strong>{' '}
                          {framework.editable_via_immutable_revision
                            ? <>This DRAFT custom bot can create a new immutable revision through <Link to={framework.edit_destination} className="font-bold text-blue-600 underline dark:text-blue-300">Your Bots</Link>.</>
                            : 'These strategy-defining values cannot be edited on this page.'}
                        </div>

                        <details className="mt-4 min-w-0 rounded-xl border border-violet-200 bg-white/70 p-4 text-xs dark:border-violet-800 dark:bg-slate-900/60">
                          <summary className="cursor-pointer font-black">Technical lineage</summary>
                          <dl className="mt-3 space-y-2 break-all">
                            <div><dt className="inline font-bold">Family: </dt><dd className="inline">{framework.family}</dd></div>
                            <div><dt className="inline font-bold">Evaluator: </dt><dd className="inline">{framework.evaluator}</dd></div>
                            <div><dt className="inline font-bold">Schema: </dt><dd className="inline">{framework.schema_version}</dd></div>
                            <div><dt className="inline font-bold">Strategy hash: </dt><dd className="inline">{framework.strategy_hash}</dd></div>
                          </dl>
                        </details>
                      </section>

                      <section aria-labelledby="risk-settings-heading" className="min-w-0 rounded-2xl border border-blue-200 bg-blue-50/60 p-5 dark:border-blue-900 dark:bg-blue-950/20 sm:p-6">
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-700 dark:text-blue-300">B. Your risk settings</p>
                        <h3 id="risk-settings-heading" className="mt-1 text-xl font-black">Practice risk and allocation</h3>
                        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">These controls limit virtual Practice activity. They do not alter the strategy’s entry or exit rules and cannot place broker orders.</p>

                        <div className="mt-5 grid gap-3 sm:grid-cols-2">
                          <div className="rounded-xl border border-blue-200 bg-white p-4 dark:border-blue-800 dark:bg-slate-900">
                            <label htmlFor="maximum-practice-allocation" className="text-sm font-black">Maximum Practice allocation (%)</label>
                            <p id="maximum-practice-allocation-help" className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">The maximum share of this bot’s virtual Practice equity available to one position.</p>
                            {allocation.editable ? (
                              <input
                                id="maximum-practice-allocation"
                                aria-describedby={`maximum-practice-allocation-help${allocationError ? ' maximum-practice-allocation-error' : ''}`}
                                aria-invalid={Boolean(allocationError)}
                                type="number"
                                inputMode="decimal"
                                min={allocation.minimum}
                                max={allocation.maximum}
                                step={allocation.step}
                                value={allocationInput}
                                onChange={(event) => {
                                  setAllocationInput(event.target.value);
                                  setAllocationError(validateAllocationPercent(event.target.value, allocation.minimum, allocation.maximum));
                                }}
                                className="mt-3 w-full rounded-lg border border-slate-300 p-3 dark:border-slate-700 dark:bg-slate-950"
                              />
                            ) : (
                              <p id="maximum-practice-allocation" className="mt-3 text-lg font-black" aria-describedby="maximum-practice-allocation-help">{formatPercent(allocation.value)}</p>
                            )}
                            {allocationError && <p id="maximum-practice-allocation-error" role="alert" className="mt-2 text-sm font-bold text-red-600 dark:text-red-300">{allocationError}</p>}
                            {!allocation.editable && <p className="mt-2 text-xs text-slate-500">Only a DRAFT bot can change this existing setting.</p>}
                          </div>
                          <ReadOnlyValue label="Maximum bot drawdown" value={formatPercent(risk.maximum_bot_drawdown.display_value)} help="Measured from this bot’s virtual account high-water mark. Reaching it schedules safety exits. The current modern default is fixed here." />
                          <ReadOnlyValue label="Account allocation boundary" value={formatPercent(risk.aggregate_allocation_limit.value)} help="All active bots together cannot reserve more than this percentage." />
                          <ReadOnlyValue label="Simultaneous positions per bot" value={risk.maximum_simultaneous_positions_per_bot.value} help="Current Practice entry authority permits at most one open position per bot." />
                        </div>

                        <p className="mt-4 rounded-xl bg-white p-4 text-sm leading-6 dark:bg-slate-900"><strong>How position size is bounded:</strong> {risk.position_sizing_explanation}</p>
                        <p className="mt-3 text-xs leading-5 text-slate-500">{advancedAuthority.legacy_account_settings.message}</p>

                        {allocation.editable && allocationDirty && (
                          <div className="mt-5">
                            <p role="status" className="mb-2 text-sm font-bold text-blue-900 dark:text-blue-200">Unsaved change: Maximum Practice allocation for {advancedAuthority.bot.name}.</p>
                            <button
                              type="button"
                              onClick={saveRiskSettings}
                              disabled={savingAllocation || Boolean(allocationError)}
                              className="inline-flex min-h-11 items-center rounded-xl bg-blue-600 px-5 py-3 font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <FaSave className="mr-2" />{savingAllocation ? 'Saving…' : 'Save risk setting'}
                            </button>
                          </div>
                        )}
                      </section>

                      <section aria-labelledby="backtesting-assumptions-heading" className="min-w-0 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 dark:border-emerald-900 dark:bg-emerald-950/20 sm:p-6">
                        <p className="text-xs font-black uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">C. Backtesting assumptions</p>
                        <h3 id="backtesting-assumptions-heading" className="mt-1 text-xl font-black">Historical simulation only</h3>
                        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">These assumptions are used when Pinngro tests this strategy against a finalized historical dataset. They do not change Practice settings.</p>
                        {!backtesting.available ? (
                          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                            <strong>Backtesting is unavailable for this strategy.</strong> {backtesting.unavailable_reason}
                          </div>
                        ) : <><div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <ReadOnlyValue label="Execution profile" value={backtesting.profile} help="Selected automatically from the strategy direction." />
                          <ReadOnlyValue label="Data timeframe" value={backtesting.data_timeframe} />
                          <ReadOnlyValue label="Execution timing" value={backtesting.execution_timing} />
                          <ReadOnlyValue label="Default allocation" value={formatPercent(backtesting.default_allocation_percent)} help="Can be changed for an individual Backtest." />
                          <ReadOnlyValue label="Slippage" value={`${backtesting.slippage.default_bps} basis points by default`} help="Modeled and configurable for each Backtest." />
                          <ReadOnlyValue label="Commission" value={`$${backtesting.commission.default_per_share} per share by default`} help="Modeled and configurable for each Backtest." />
                          <ReadOnlyValue label="Historical prices" value={backtesting.price_adjustment} />
                          <ReadOnlyValue label="Spread" value="Not separately modeled" />
                          {backtesting.borrow_cost.applicable && <ReadOnlyValue label="Short borrow cost" value="Not currently modeled" help="The short profile does not claim to simulate real borrowing, locate, dividend, financing, or margin costs." />}
                          <ReadOnlyValue label="Position limit" value={`${backtesting.maximum_positions} at a time`} help={backtesting.cash_and_collateral} />
                        </div>
                        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">Backtest inputs are pinned with the result so exact replay can verify them later.</p>
                          <Link to="/backtests" className="inline-flex min-h-11 items-center justify-center rounded-xl bg-emerald-700 px-5 py-3 text-center font-bold text-white">Open Backtesting</Link>
                        </div>
                        </>}
                      </section>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default SettingsPage;
